/**
 * TabNav Component
 * Reusable tab navigation component with consistent styling
 *
 * Usage:
 * const tabs = [
 *     { key: 'all', label: 'All Items' },
 *     { key: 'active', label: 'Active', count: 5 },
 *     { key: 'archived', label: 'Archived' },
 * ];
 *
 * <TabNav
 *     tabs={tabs}
 *     activeTab={currentTab}
 *     onChange={setCurrentTab}
 * />
 */

import { ReactNode, useEffect, useRef, useState } from 'react';

export interface Tab<T extends string = string> {
    key: T;
    label: string;
    count?: number;
    icon?: ReactNode;
    disabled?: boolean;
}

interface TabNavProps<T extends string = string> {
    tabs: Tab<T>[];
    activeTab: T;
    onChange: (tab: T) => void;
    className?: string;
    variant?: 'underline' | 'pills';
    size?: 'sm' | 'md' | 'lg';
}

// Tighter on phones so more tabs fit before the row has to scroll.
const sizeStyles = {
    sm: 'px-3 py-2 text-xs',
    md: 'px-3 py-2.5 text-xs sm:px-4 sm:py-3 sm:text-sm',
    lg: 'px-4 py-3 text-sm sm:px-6 sm:py-4 sm:text-base',
};

/** Slack (px) under which a row counts as scrolled to its end. */
const SCROLL_END_TOLERANCE_PX = 2;

/**
 * Drives the underline variant's scroll row: reports whether tabs are
 * hidden past the right edge (so a fade can say "more here") and
 * brings the active tab into view when it changes. Scrolls the row
 * only — never the page — so a tab row below the fold stays put.
 */
function useTabScrollRow(activeTab: string) {
    const rowRef = useRef<HTMLDivElement>(null);
    const [hasHiddenTabs, setHasHiddenTabs] = useState(false);

    useEffect(() => {
        const row = rowRef.current;
        if (!row) return;
        const measure = () => {
            setHasHiddenTabs(row.scrollWidth - row.clientWidth - row.scrollLeft > SCROLL_END_TOLERANCE_PX);
        };
        row.addEventListener('scroll', measure, { passive: true });
        // Observing the row covers viewport changes; observing its content
        // covers tabs appearing or growing (counts arriving, labels changing).
        const observer = new ResizeObserver(measure);
        observer.observe(row);
        if (row.firstElementChild) observer.observe(row.firstElementChild);
        return () => {
            row.removeEventListener('scroll', measure);
            observer.disconnect();
        };
    }, []);

    useEffect(() => {
        const row = rowRef.current;
        const active = row?.querySelector<HTMLElement>('[aria-selected="true"]');
        if (!row || !active) return;
        const tabLeft = active.offsetLeft;
        const tabRight = tabLeft + active.offsetWidth;
        if (tabLeft < row.scrollLeft) {
            row.scrollLeft = tabLeft;
        } else if (tabRight > row.scrollLeft + row.clientWidth) {
            row.scrollLeft = tabRight - row.clientWidth;
        }
    }, [activeTab]);

    return { rowRef, hasHiddenTabs };
}

export function TabNav<T extends string = string>({
    tabs,
    activeTab,
    onChange,
    className = '',
    variant = 'underline',
    size = 'md',
}: TabNavProps<T>) {
    const { rowRef, hasHiddenTabs } = useTabScrollRow(activeTab);

    if (variant === 'pills') {
        return (
            <div className={`flex flex-wrap gap-2 ${className}`} role="tablist" aria-label="Tab navigation">
                {tabs.map(tab => (
                    <button
                        key={tab.key}
                        role="tab"
                        aria-selected={activeTab === tab.key}
                        data-testid={`tab-${tab.key}`}
                        onClick={() => !tab.disabled && onChange(tab.key)}
                        disabled={tab.disabled}
                        className={`
                            ${sizeStyles[size]}
                            font-medium rounded-lg transition-colors
                            inline-flex items-center gap-2
                            ${tab.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                            ${activeTab === tab.key
                                ? 'bg-primary text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }
                        `.trim().replace(/\s+/g, ' ')}
                    >
                        {tab.icon}
                        <span>{tab.label}</span>
                        {tab.count !== undefined && (
                            <span className={`
                                ml-1 px-1.5 py-0.5 text-xs rounded-full
                                ${activeTab === tab.key
                                    ? 'bg-white/20 text-white'
                                    : 'bg-gray-200 text-gray-600'
                                }
                            `.trim().replace(/\s+/g, ' ')}>
                                {tab.count}
                            </span>
                        )}
                    </button>
                ))}
            </div>
        );
    }

    // Default: underline variant. Tabs grow to their `whitespace-nowrap`
    // label width, so on narrow viewports four+ tabs can easily exceed
    // the card width — without `overflow-x-auto` on the row, the whole
    // card would stretch past the viewport and push its OTHER children
    // (search bars, filter dropdowns) off-screen on the left. Scrolling
    // is constrained to the tab row only; a fade over the right edge
    // tells the reader there are more tabs to swipe to, and the row
    // scrolls the active tab into view when it changes.
    return (
        <div className={`relative border-b border-gray-200 ${className}`}>
            <div ref={rowRef} className="relative -mb-px overflow-x-auto scrollbar-none">
                <nav className="flex min-w-max" role="tablist" aria-label="Tab navigation">
                    {tabs.map(tab => (
                        <button
                            key={tab.key}
                            role="tab"
                            aria-selected={activeTab === tab.key}
                            data-testid={`tab-${tab.key}`}
                            onClick={() => !tab.disabled && onChange(tab.key)}
                            disabled={tab.disabled}
                            className={`
                                ${sizeStyles[size]}
                                font-medium border-b-2 transition-colors whitespace-nowrap
                                inline-flex items-center gap-2
                                ${tab.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                ${activeTab === tab.key
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }
                            `.trim().replace(/\s+/g, ' ')}
                        >
                            {tab.icon}
                            <span>{tab.label}</span>
                            {tab.count !== undefined && (
                                <span className={`
                                    ml-1 px-1.5 py-0.5 text-xs rounded-full
                                    ${activeTab === tab.key
                                        ? 'bg-primary/10 text-primary'
                                        : 'bg-gray-100 text-gray-500'
                                    }
                                `.trim().replace(/\s+/g, ' ')}>
                                    {tab.count}
                                </span>
                            )}
                        </button>
                    ))}
                </nav>
            </div>
            {hasHiddenTabs && (
                <div
                    data-testid="tabnav-more"
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-white to-transparent"
                />
            )}
        </div>
    );
}

export default TabNav;
